import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import {
  COURSE_CYCLE_COLUMN_LABEL,
  COURSE_CYCLE_TOOLTIP,
  formatCycleRange,
  getCourseCycleStats,
  getCourseCycleTooltip,
} from '../../utils/courseCycleProgress';

/** Compact table header with optional info tooltip. */
export function CourseCycleColumnHeader() {
  return (
    <Tooltip title={COURSE_CYCLE_TOOLTIP} arrow placement="top">
      <Stack
        direction="row"
        spacing={0.35}
        alignItems="center"
        component="span"
        sx={{ cursor: 'help', whiteSpace: 'nowrap' }}
      >
        <span>{COURSE_CYCLE_COLUMN_LABEL}</span>
        <InfoOutlined sx={{ fontSize: 14, opacity: 0.55 }} aria-hidden />
      </Stack>
    </Tooltip>
  );
}

/**
 * Two-line cell: "1 / 13" + "planned" (and optional date range on wider layouts).
 */
export function CourseCycleProgressCell({
  classesCompleted,
  progressDenominator,
  progressWindowStart,
  progressWindowEnd,
  showRange = true,
}) {
  const { done, planned, hasCycle } = getCourseCycleStats(classesCompleted, progressDenominator);
  const rangeLabel = formatCycleRange(progressWindowStart, progressWindowEnd);
  const tooltip = getCourseCycleTooltip({ done, planned, hasCycle, rangeLabel });

  const subline = hasCycle
    ? showRange && rangeLabel
      ? `planned · ${rangeLabel}`
      : 'planned in cycle'
    : null;

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box component="span" sx={{ display: 'inline-block', cursor: 'help', minWidth: 56 }}>
        {hasCycle ? (
          <Stack spacing={0.2}>
            <Typography variant="body2" fontWeight={700} lineHeight={1.25} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {done} / {planned}
            </Typography>
            {subline ? (
              <Typography
                variant="caption"
                color="text.secondary"
                lineHeight={1.2}
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  display: 'block',
                  maxWidth: { xs: 88, sm: 160 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subline}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2" fontWeight={600}>
            {done}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/**
 * Inline fraction for progress bars: "1/13" with tooltip.
 */
export function CourseCycleProgressFraction({
  classesCompleted,
  progressDenominator,
  progressWindowStart,
  progressWindowEnd,
}) {
  const { done, planned, hasCycle } = getCourseCycleStats(classesCompleted, progressDenominator);
  const rangeLabel = formatCycleRange(progressWindowStart, progressWindowEnd);
  const tooltip = getCourseCycleTooltip({ done, planned, hasCycle, rangeLabel });
  const label = hasCycle ? `${done}/${planned}` : String(done);

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Typography
        variant="caption"
        fontWeight={900}
        color="text.secondary"
        component="span"
        sx={{ cursor: 'help', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Tooltip>
  );
}
